import { useDiscardChangesGuard } from '@components/v2/ui/DiscardChangesModal/hooks/useDiscardChangesGuard';
import { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Catalog, EmphasisCatalog } from '../../catalogs/catalog.types';
import { hasEmphasisCatalogChanges } from '../emphasisCatalogEdit.utils';

type UseEmphasisCatalogEditParams = {
  isSaving: boolean;
  emphasisCatalogs: EmphasisCatalog[];
  onSave: (emphasisCatalogs: EmphasisCatalog[]) => void;
  onClose: () => void;
};

const useEmphasisCatalogEdit = ({
  isSaving,
  emphasisCatalogs,
  onSave,
  onClose,
}: UseEmphasisCatalogEditParams) => {
  const [selectedCatalogs, setSelectedCatalogs] = useState(emphasisCatalogs);
  const [syncedCatalogs, setSyncedCatalogs] = useState(emphasisCatalogs);

  if (emphasisCatalogs !== syncedCatalogs) {
    setSyncedCatalogs(emphasisCatalogs);
    setSelectedCatalogs(emphasisCatalogs);
  }

  const { requestClose, discardModalProps } = useDiscardChangesGuard({
    hasUnsavedChanges: hasEmphasisCatalogChanges({
      selectedCatalogs,
      savedCatalogs: emphasisCatalogs,
    }),
    onDiscard: onClose,
  });

  const selectedCatalogIds = selectedCatalogs.map((item) => item.catalog.id);

  const removeCatalog = (catalogId: string) =>
    setSelectedCatalogs((current) => current.filter((item) => item.catalog.id !== catalogId));

  const handleRemove = (emphasisCatalog: EmphasisCatalog) =>
    removeCatalog(emphasisCatalog.catalog.id);

  const toggleSelectCatalog = (catalog: Catalog, isSelected: boolean) => {
    if (isSelected) {
      removeCatalog(catalog.id);
      return;
    }

    setSelectedCatalogs((current) => [
      ...current,
      { id: uuidv4(), catalog, position: current.length + 1 },
    ]);
  };

  const handleRequestClose = () => {
    if (isSaving) {
      return;
    }

    requestClose();
  };

  const handleExited = () => setSelectedCatalogs(emphasisCatalogs);

  const save = () => onSave(selectedCatalogs);

  return {
    selectedCatalogs,
    selectedCatalogIds,
    discardModalProps,
    setSelectedCatalogs,
    toggleSelectCatalog,
    handleRemove,
    handleExited,
    handleRequestClose,
    save,
  };
};

export default useEmphasisCatalogEdit;
